import React, { useEffect, useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faSortUp,
  faSortDown,
  faSort,
} from "@fortawesome/free-solid-svg-icons";
import { colors, constants } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import {
  SelectDropdown,
  SearchableSelectDropdown,
  FilterBar as SharedFilterBar,
  DropdownContainer,
  ClearAllButton,
} from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { FeedbackCTA } from "components/shared/FeedbackCTA/FeedbackCTA";
import { saveFilters } from "utils/persistFilters";
import { localIsoDate } from "utils/localDate";
import { useFilterPersistence } from "hooks/useFilterPersistence/useFilterPersistence";
import { pageTitleInset } from "components/pageTitleInset";

const formatWeekHeader = (isoDate) => {
  const d = new Date(isoDate + "T00:00:00");
  return `Week of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

// The three dosage tags, as the frame's "Tag - Level/Large" draws them
// (4422:118107 / 118110 / 118113): fill, 1px rule and text.
export const DOSAGE_TAGS = {
  green: { label: "Green", color: "#006B3B", bg: "#E9FFF5", border: "#76D9AD" },
  yellow: { label: "Yellow", color: "#9B6B03", bg: "#FEFCE8", border: "#EAB308" },
  red: { label: "Red", color: "#BC165D", bg: "#FFF2F5", border: "#FEADC3" },
};

const getCellStyle = (cell) => {
  if (!cell || cell.total_minutes === 0) {
    return { text: "", cellBg: "#FFFEFE", empty: true };
  }
  const label = `${cell.total_minutes} ${cell.total_minutes === 1 ? "Min" : "Mins"}`;
  const tag =
    cell.total_minutes >= 45 && cell.session_count >= 2
      ? DOSAGE_TAGS.green
      : cell.total_minutes >= 10
        ? DOSAGE_TAGS.yellow
        : DOSAGE_TAGS.red;
  return { text: label, ...tag, cellBg: "#FFFEFE", empty: false };
};

// "5 Hrs 53 Mins" for the Total row; minutes alone under an hour.
export const formatTotalMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const mins = `${rest} ${rest === 1 ? "Min" : "Mins"}`;
  return hours > 0 ? `${hours} ${hours === 1 ? "Hr" : "Hrs"} ${mins}` : mins;
};

const toIso = localIsoDate;

const getMonday = (d) => {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const mon = new Date(d);
  mon.setDate(mon.getDate() + diff);
  return mon;
};

const resolveDateRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = getMonday(today);
  const endDate = new Date(monday);
  endDate.setDate(endDate.getDate() + 7);

  switch (preset) {
    case "last_4_weeks": {
      const start = new Date(monday);
      start.setDate(start.getDate() - 21);
      return { start: toIso(start), end: toIso(endDate) };
    }
    case "last_8_weeks": {
      const start = new Date(monday);
      start.setDate(start.getDate() - 49);
      return { start: toIso(start), end: toIso(endDate) };
    }
    case "this_school_year": {
      const year = today.getMonth() >= 7 ? today.getFullYear() : today.getFullYear() - 1;
      return { start: `${year}-08-01`, end: toIso(endDate) };
    }
    case "all_time":
    default:
      return { start: "2020-01-01", end: toIso(endDate) };
  }
};

const DATE_RANGE_OPTIONS = [
  { label: "Last 4 weeks", value: "last_4_weeks" },
  { label: "Last 8 weeks", value: "last_8_weeks" },
  { label: "This school year", value: "this_school_year" },
  { label: "All time", value: "all_time" },
];

export const StudentActivity = ({ educatorId = null, sharedClassId, onClassChange, embedded = false }) => {
  const history = useHistory();
  const {
    classes,
    selectedClassId,
    setSelectedClassId,
    savedState,
    sharedState,
    hasSharedContext,
    filtersReady,
    hydrated,
    markHydrated,
    readyToPersist,
    error: filtersError,
  } = useFilterPersistence({
    pageKey: "student_activity",
    educatorId,
    sharedClassId,
    onClassChange,
  });
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [dateRange, setDateRange] = useState("last_4_weeks");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);
  const loading = !filtersReady;

  // Surface class-resolution failures through the same error channel as data fetches.
  useEffect(() => {
    if (filtersError) {
      const msg = filtersError?.detail || filtersError?.message || "An unknown error occurred.";
      logError("Failed to fetch class data", filtersError, { category: LogCategory.ERROR });
      setError(msg);
    }
  }, [filtersError]);

  // Hydrate page-specific fields once the hook finishes resolving the class. Shared
  // is authoritative for student when it has class context; saved handles date
  // range regardless (date isn't cross-tab).
  useEffect(() => {
    if (!filtersReady || hydrated) return;
    if (savedState) {
      if (savedState.startDate && savedState.endDate) {
        setStartDate(savedState.startDate);
        setEndDate(savedState.endDate);
        setDateRange(null);
      } else if (savedState.dateRange) {
        setDateRange(savedState.dateRange);
      }
      if (!embedded && !hasSharedContext && savedState.selectedStudentId) {
        setSelectedStudentId(savedState.selectedStudentId);
      }
    }
    // Embedded (Class Reports) shows no Student filter, so a remembered
    // student must not silently narrow the table.
    if (!embedded && hasSharedContext) {
      setSelectedStudentId(sharedState.selectedStudentId || null);
    }
    markHydrated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);

  useEffect(() => {
    if (selectedClassId && readyToPersist) {
      saveFilters("student_activity", {
        selectedClassId,
        dateRange,
        startDate,
        endDate,
        selectedStudentId,
      });
    }
  }, [selectedClassId, dateRange, startDate, endDate, selectedStudentId, readyToPersist]);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append("start_date", startDate);
      params.append("end_date", endDate);
    } else {
      const resolved = resolveDateRange(dateRange || "last_4_weeks");
      params.append("start_date", resolved.start);
      params.append("end_date", resolved.end);
    }
    if (selectedStudentId) {
      params.append("student_id", selectedStudentId);
    }
    if (educatorId) {
      params.append("educator_id", educatorId);
    }
    return params;
  }, [dateRange, startDate, endDate, selectedStudentId, educatorId]);

  // Activity data, cached per (class, dates, student, sort): a revisited
  // combination paints instantly while revalidating in the background; the
  // spinner only covers an uncached one (OKO2-3684). A 404 is an ordinary
  // empty report, not an error.
  const activityQuery = useQuery({
    queryKey: [
      "student-activity",
      selectedClassId, dateRange, startDate, endDate,
      selectedStudentId, sortBy, sortOrder, educatorId ?? null,
    ],
    queryFn: async () => {
      const params = buildFilterParams();
      if (sortBy) {
        params.append("sort_by", sortBy);
      }
      params.append("sort_order", sortOrder);
      const queryStr = params.toString();
      const url = `class-report/${selectedClassId}/student-activity${queryStr ? `?${queryStr}` : ""}`;
      try {
        return await request(url, { method: "GET" });
      } catch (e) {
        if (e?.status === 404) return null;
        throw e;
      }
    },
    enabled: Boolean(selectedClassId) && filtersReady,
    staleTime: 0,
  });
  const activityData = activityQuery.data ?? null;
  const dataLoading = activityQuery.isInitialLoading;

  // The old fetch set/cleared the error inline; the query reports it as state,
  // so mirror that into the page's shared error channel.
  useEffect(() => {
    const e = activityQuery.error;
    if (!e) {
      // Clear only what a fetch actually resolved — a disabled query must not
      // wipe the class-resolution error set above.
      if (activityQuery.isFetched) setError(null);
      return;
    }
    if (e?.status === 403) {
      setError("You don't have access to this classroom.");
    } else {
      logError("Failed to fetch student activity", e, {
        category: LogCategory.ERROR,
      });
      setError(e?.detail || e?.message || "Failed to load activity data.");
    }
  }, [activityQuery.error, activityQuery.isFetched]);

  const handleClassChange = (classId) => {
    setSelectedClassId(classId);
    setSelectedStudentId(null);
    setStartDate(null);
    setEndDate(null);
    setDateRange("last_4_weeks");
    setSortBy(null);
    setSortOrder("desc");
  };

  // Student dropdown options, cached per class.
  const { data: students = [] } = useQuery({
    queryKey: ["session-history-students", selectedClassId, educatorId ?? null],
    queryFn: async () => {
      try {
        const url = educatorId
          ? `account/session_history/students?classroom_id=${selectedClassId}&educator_id=${educatorId}`
          : `account/session_history/students?classroom_id=${selectedClassId}`;
        return (await request(url, { method: "GET" })) || [];
      } catch (e) {
        // The dropdown degrades to empty rather than failing the page.
        logError("Failed to fetch students for activity", e, {
          category: LogCategory.ERROR,
        });
        return [];
      }
    },
    enabled: Boolean(selectedClassId),
    staleTime: 0,
  });

  const handleClearAll = () => {
    setSelectedStudentId(null);
    setStartDate(null);
    setEndDate(null);
    setDateRange("all_time");
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const handleExport = async () => {
    if (!selectedClassId || exportLoading) return;
    setExportLoading(true);
    try {
      const params = buildFilterParams();
      const queryStr = params.toString();
      const url = `class-report/${selectedClassId}/student-activity/export${queryStr ? `?${queryStr}` : ""}`;
      const response = await request(url, { method: "GET", raw: true });

      const blob = await response.blob();
      const filename = response.headers?.get("content-disposition")?.match(/filename="?([^"]+)"?/)?.[1]
        || "student-activity.csv";
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      logError("Failed to export student activity CSV", e, {
        category: LogCategory.ERROR,
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleCellClick = (student, weekDate) => {
    const cell = student.weeks?.[weekDate];
    if (!cell || cell.total_minutes === 0) return;
    const endDate = new Date(weekDate + "T00:00:00");
    endDate.setDate(endDate.getDate() + 6);
    const params = new URLSearchParams();
    params.set("student_id", student.student_id);
    params.set("start_date", weekDate);
    params.set("end_date", localIsoDate(endDate));
    if (selectedClassId) {
      params.set("classroom_id", selectedClassId);
    }
    if (educatorId) {
      params.set("educator_id", educatorId);
    }
    // Session Reports opens By Lesson by default; this link is about sessions.
    params.set("tab", "groups");
    history.push(`${constants.SESSION_HISTORY_PATH}?${params.toString()}`);
  };

  const handleCellHover = (e, cell) => {
    if (!cell || cell.total_minutes === 0) {
      setTooltip(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.right;
    const flipLeft = spaceRight < 120;
    setTooltip({
      text: `${cell.session_count} Session${cell.session_count !== 1 ? "s" : ""}`,
      x: flipLeft ? rect.left : rect.right,
      y: rect.top + rect.height / 2,
      flipLeft,
    });
  };

  const selectedClass = classes.find((c) => c.id === selectedClassId);
  const weeks = activityData?.weeks || [];
  const studentRows = activityData?.students || [];

  const displayWeeks = (() => {
    if (weeks.length === 0) return weeks;

    const activePreset = startDate ? null : (dateRange || "last_4_weeks");
    const isAllTime = activePreset === "all_time";

    // Resolve the range boundaries.
    // resolveDateRange sets end to (current Monday + 7), i.e. the start of
    // the week *after* the last included week, so step back 1 day before
    // snapping to Monday to land on the correct final week.
    let rangeStart, rangeEnd;
    if (startDate && endDate) {
      rangeStart = toIso(getMonday(new Date(startDate + "T00:00:00")));
      rangeEnd = new Date(endDate + "T00:00:00");
    } else {
      const resolved = resolveDateRange(activePreset);
      rangeStart = toIso(getMonday(new Date(resolved.start + "T00:00:00")));
      rangeEnd = new Date(resolved.end + "T00:00:00");
    }
    rangeEnd.setDate(rangeEnd.getDate() - 1);
    const endMonday = toIso(getMonday(rangeEnd));

    // For bounded ranges (4w, 8w, school year, custom) show every week from
    // the range start. For "all time" only fill gaps starting from the first
    // week with data to avoid a wall of empty columns.
    const firstMonday = isAllTime ? weeks[0] : rangeStart;

    const allMondays = [];
    const cursor = new Date(firstMonday + "T00:00:00");
    while (toIso(cursor) <= endMonday) {
      allMondays.push(toIso(cursor));
      cursor.setDate(cursor.getDate() + 7);
    }
    return allMondays;
  })();

  if (loading) {
    return (
      <Container $embedded={embedded}>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" color={colors.blue500} />
        </LoadingContainer>
      </Container>
    );
  }

  if (error && !activityData) {
    return (
      <Container $embedded={embedded}>
        {!embedded && (
          <PageHeader>
            <HeaderContent>
              <Title>Student Activity</Title>
            </HeaderContent>
          </PageHeader>
        )}
        <ContentArea>
          <ErrorContainer>{error}</ErrorContainer>
        </ContentArea>
      </Container>
    );
  }

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <PageHeader>
          <HeaderContent>
            <Title>Student Activity</Title>
          </HeaderContent>
        </PageHeader>
      )}

      <ContentArea>
        <SharedFilterBar>
          {!embedded && (
            <SelectDropdown
              label="Class"
              value={selectedClassId}
              displayText={classDisplayName(selectedClass) || "Select a class"}
              options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
              groupBySchoolYear
              onChange={handleClassChange}
            />
          )}
          {!embedded && (
            <SearchableSelectDropdown
              label="Student"
              value={selectedStudentId}
              displayText={
                selectedStudentId
                  ? students.find((s) => s.student_id === selectedStudentId)?.display_name || "Unknown"
                  : "All Students"
              }
              options={[
                { value: null, label: "All Students" },
                ...students.map((s) => ({
                  value: s.student_id,
                  label: s.display_name || "Unknown Student",
                })),
              ]}
              onChange={setSelectedStudentId}
              clearable
            />
          )}
          <DateRangeSlot $embedded={embedded}>
          <SelectDropdown
            label="Date Range"
            value={dateRange || "custom"}
            displayText={
              startDate
                ? (() => {
                    const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    if (endDate) {
                      const diff = Math.round((new Date(endDate + "T00:00:00") - new Date(startDate + "T00:00:00")) / 86400000);
                      if (diff > 7) return `${fmt(startDate)} – ${fmt(endDate)}`;
                    }
                    return `Week of ${fmt(startDate)}`;
                  })()
                : DATE_RANGE_OPTIONS.find((o) => o.value === dateRange)?.label || "Last 4 weeks"
            }
            options={DATE_RANGE_OPTIONS}
            onChange={(value) => {
              setStartDate(null);
              setEndDate(null);
              setDateRange(value);
            }}
            clearable
            clearValue="all_time"
          />
          </DateRangeSlot>
          {!embedded && <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>}
          <ExportButtonWrapper>
            <ExportButton onClick={handleExport} disabled={exportLoading || !selectedClassId}>
              {exportLoading && <FontAwesomeIcon icon={faSpinner} spin />}
              Export .csv
            </ExportButton>
          </ExportButtonWrapper>
        </SharedFilterBar>

        <ProTipBanner>
          <ProTipLabel>Pro Tip:</ProTipLabel>{" "}
          We recommend 45+ minutes of OKO per week so that students have enough time to practice
          the math and improve their collaboration and math discourse skills.
        </ProTipBanner>

        <ColorKeyRow>
          <KeyLabel>Key:</KeyLabel>
          <KeyItems>
            <KeyItem>
              <MinuteTag $bg={DOSAGE_TAGS.green.bg} $color={DOSAGE_TAGS.green.color} $border={DOSAGE_TAGS.green.border}>
                {DOSAGE_TAGS.green.label}
              </MinuteTag>
              <KeyText>= 45+ mins and 2+ sessions</KeyText>
            </KeyItem>
            <KeyItem>
              <MinuteTag $bg={DOSAGE_TAGS.yellow.bg} $color={DOSAGE_TAGS.yellow.color} $border={DOSAGE_TAGS.yellow.border}>
                {DOSAGE_TAGS.yellow.label}
              </MinuteTag>
              <KeyText>= 10 mins to &lt;45 mins</KeyText>
            </KeyItem>
            <KeyItem>
              <MinuteTag $bg={DOSAGE_TAGS.red.bg} $color={DOSAGE_TAGS.red.color} $border={DOSAGE_TAGS.red.border}>
                {DOSAGE_TAGS.red.label}
              </MinuteTag>
              <KeyText>= Less than 10 mins</KeyText>
            </KeyItem>
          </KeyItems>
        </ColorKeyRow>

        {dataLoading && (
          <LoadingContainer>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color={colors.blue500} />
          </LoadingContainer>
        )}

        {!dataLoading && (
          <TableWrapper>
            <HeatmapTable>
              <thead>
                <tr>
                  <SortableHeader scope="col" onClick={() => handleSort("name")} $isStudent>
                    <span>Student</span>
                    <SortIcon
                      icon={sortBy === "name" ? (sortOrder === "asc" ? faSortUp : faSortDown) : faSort}
                      $isActive={sortBy === "name"}
                    />
                  </SortableHeader>
                  {displayWeeks.map((week) => (
                    <SortableHeader scope="col" key={week} onClick={() => handleSort(week)}>
                      <span>{formatWeekHeader(week)}</span>
                      <SortIcon
                        icon={sortBy === week ? (sortOrder === "asc" ? faSortUp : faSortDown) : faSort}
                        $isActive={sortBy === week}
                      />
                    </SortableHeader>
                  ))}
                </tr>
              </thead>
              <tbody>
                {studentRows.length === 0 && (
                  <tr>
                    <EmptyCell colSpan={displayWeeks.length + 1}>
                      No data available for the selected filters.
                    </EmptyCell>
                  </tr>
                )}
                {studentRows.map((student, index) => (
                  <TableRow key={student.student_id} $even={index % 2 === 0}>
                    <StudentNameCell as="th" scope="row">{student.student_name}</StudentNameCell>
                    {displayWeeks.map((week) => {
                      const cell = student.weeks?.[week];
                      const style = getCellStyle(cell);
                      return (
                        <MinuteCell
                          key={week}
                          $cellBg={style.cellBg}
                          $clickable={!style.empty}
                          role={!style.empty ? "button" : undefined}
                          tabIndex={!style.empty ? 0 : undefined}
                          aria-label={!style.empty ? `${student.student_name}, ${formatWeekHeader(week)}: ${style.text}. View session report` : undefined}
                          onClick={() => handleCellClick(student, week)}
                          onKeyDown={(e) => {
                            if (!style.empty && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              handleCellClick(student, week);
                            }
                          }}
                        >
                          {!style.empty && (
                            <MinuteTag
                              $color={style.color}
                              $bg={style.bg}
                              $border={style.border}
                              onMouseEnter={(e) => handleCellHover(e, cell)}
                              onMouseLeave={() => setTooltip(null)}
                            >
                              {style.text}
                            </MinuteTag>
                          )}
                        </MinuteCell>
                      );
                    })}
                  </TableRow>
                ))}
              </tbody>
              {studentRows.length > 0 && (
                <tfoot>
                  <tr>
                    <TotalLabelCell scope="row">Total</TotalLabelCell>
                    {displayWeeks.map((week) => (
                      <TotalCell key={week}>
                        {formatTotalMinutes(
                          studentRows.reduce((sum, s) => sum + (s.weeks?.[week]?.total_minutes || 0), 0)
                        )}
                      </TotalCell>
                    ))}
                  </tr>
                </tfoot>
              )}
            </HeatmapTable>
          </TableWrapper>
        )}
        <FeedbackCTA
          reportLocation="Student Activity"
          contextMessage={`[Student Activity] Class: ${selectedClass?.name || "N/A"} (ID: ${selectedClassId || "N/A"})`}
        />
      </ContentArea>

      {tooltip && (
        <Tooltip
          ref={tooltipRef}
          $flipLeft={tooltip.flipLeft}
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: tooltip.flipLeft
              ? "translate(-100%, -50%)"
              : "translateY(-50%)",
          }}
        >
          {tooltip.text}
        </Tooltip>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${(p) => (p.$embedded ? "0" : "calc(100vh - 92px)")};
  background-color: ${colors.milkGlass200};
`;

const PageHeader = styled.div`
  background-color: ${colors.nightlyWoods500};
  padding: 24px 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px 0 ${pageTitleInset};
`;

const Title = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

const ContentArea = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// On the Class Reports Dosage tab the date range is the only filter, and the
// frame (4422:118099) caps the select at 250px with Export at the far right.
const DateRangeSlot = styled.div`
  flex: 1;
  min-width: 0;

  & > * {
    max-width: ${(p) => (p.$embedded ? "250px" : "none")};
  }
`;

const ExportButtonWrapper = styled(DropdownContainer)`
  flex: 0 0 auto;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  height: 40px;
  border-radius: 6px;
  border: none;
  background-color: ${colors.blue500};
  color: ${colors.gray50};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 22px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProTipBanner = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: #002E19;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const ProTipLabel = styled.span`
  font-weight: 700;
`;

const ColorKeyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: "Inter", sans-serif;
  color: #002E19;
`;

const KeyLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const KeyItems = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const KeyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KeyText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
`;

const ErrorContainer = styled.div`
  padding: 32px;
  text-align: center;
  color: ${colors.orange600};
  font-size: 16px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

// `separate` rather than `collapse`: with collapsed borders a sticky cell's
// own border stays behind while the cell pins, and the right rule on the
// Student column is the cue that it is pinned.
const HeatmapTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

// The pinned Student column (Figma 4422:118117 / 118123): a fixed 159px that
// survives overflow -- `width` alone is dropped by auto table layout once the
// week columns no longer fit -- with a rule to its right.
const PINNED_COLUMN = `
  position: sticky;
  left: 0;
  z-index: 1;
  width: 159px;
  min-width: 159px;
  max-width: 159px;
  box-sizing: border-box;
  border-right: 1px solid #858B87;
`;

const SortableHeader = styled.th`
  background-color: #002E19;
  color: white;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  ${(props) => props.$isStudent && PINNED_COLUMN}

  &:hover {
    background-color: #003D20;
  }
`;

const SortIcon = styled(FontAwesomeIcon)`
  margin-left: 6px;
  font-size: 12px;
  opacity: ${(props) => (props.$isActive ? 1 : 0.5)};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #858B87;
`;

const StudentNameCell = styled.td`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #4F4F4F;
  padding: 10px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #F2F2F2;
  border-bottom: 1px solid #858B87;
  ${PINNED_COLUMN}
`;

const MinuteCell = styled.td`
  font-family: "Inter", sans-serif;
  padding: 10px 12px;
  background-color: ${(props) => props.$cellBg};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  white-space: nowrap;
  border-bottom: 1px solid #858B87;

  &:hover {
    ${(props) => props.$clickable && "opacity: 0.8;"}
  }
`;

const MinuteTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 11px;
  border: 1px solid ${(props) => props.$border};
  background-color: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

// The Total row (Figma 4422:118194): green/100 across, the label pinned
// with the Student column and bold.
const TotalLabelCell = styled.th`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #00140B;
  text-align: left;
  padding: 10px 12px;
  white-space: nowrap;
  background-color: #E7E7E7;
  border-bottom: 1px solid #858B87;
  ${PINNED_COLUMN}
`;

const TotalCell = styled.td`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #002E19;
  padding: 10px 12px;
  white-space: nowrap;
  background-color: #E7E7E7;
  border-bottom: 1px solid #858B87;
`;

const EmptyCell = styled.td`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  color: ${colors.milkGlass950};
  text-align: center;
  padding: 32px 16px;
`;

const Tooltip = styled.div`
  position: fixed;
  background-color: ${colors.blue100};
  color: #00140B;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 8px 12px;
  border-radius: 12px;
  pointer-events: none;
  z-index: 2000;
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 12px;
    background-color: ${colors.blue100};
    ${(props) => props.$flipLeft
      ? `right: -8px; clip-path: polygon(0 0, 100% 50%, 0 100%);`
      : `left: -8px; clip-path: polygon(100% 0, 0 50%, 100% 100%);`}
  }
`;

